import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { useToast } from '@/hooks/use-toast'
import { useCreateEmergencyContact, useUpdateEmergencyContact } from '@/hooks/use-api'
import type { EmergencyContact } from '@/types/hr'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  employeeId: string
  contact?: EmergencyContact | null
}

export function EmergencyContactForm({ open, onOpenChange, employeeId, contact }: Props) {
  const { toast } = useToast()
  const createMutation = useCreateEmergencyContact()
  const updateMutation = useUpdateEmergencyContact()

  const [name, setName] = useState('')
  const [relationship, setRelationship] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [isPrimary, setIsPrimary] = useState(false)
  const [notes, setNotes] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setName(contact?.name ?? '')
      setRelationship(contact?.relationship ?? '')
      setPhone(contact?.phone ?? '')
      setEmail(contact?.email ?? '')
      setIsPrimary(contact?.is_primary ?? false)
      setNotes(contact?.notes ?? '')
      setErrors({})
    }
  }, [open, contact])

  const isEdit = !!contact?.id

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault()
    const e: Record<string, string> = {}
    if (!name.trim()) e.name = 'Name is required'
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Invalid email'
    setErrors(e)
    if (Object.keys(e).length > 0) return

    const data: Partial<EmergencyContact> = {
      name: name.trim(),
      relationship: relationship.trim() || null,
      phone: phone.trim() || null,
      email: email.trim() || null,
      is_primary: isPrimary,
      notes: notes.trim() || null,
    }

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ employeeId, id: contact!.id, data })
      } else {
        await createMutation.mutateAsync({ employeeId, data })
      }
      toast({ title: isEdit ? 'Contact updated' : 'Contact added' })
      onOpenChange(false)
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      toast({
        title: 'Error',
        description: msg || 'Failed to save contact',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Emergency Contact' : 'New Emergency Contact'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="ec-name">Name *</Label>
            <Input
              id="ec-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-invalid={!!errors.name}
            />
            {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
          </div>
          <div>
            <Label htmlFor="ec-rel">Relationship</Label>
            <Input
              id="ec-rel"
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
              placeholder="e.g. Spouse, Parent"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ec-phone">Phone</Label>
              <Input id="ec-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="ec-email">Email</Label>
              <Input
                id="ec-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={!!errors.email}
              />
              {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
            </div>
          </div>
          <div>
            <Label htmlFor="ec-notes">Notes</Label>
            <Textarea
              id="ec-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="ec-primary"
              checked={isPrimary}
              onCheckedChange={(v) => setIsPrimary(!!v)}
            />
            <Label htmlFor="ec-primary" className="font-normal">
              Primary contact
            </Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {isEdit ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
